import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { settingsAPI } from '@/lib/api';
import { resolveImageUrl } from '@/lib/imageUtils';
import {
  LayoutDashboard, Package, Users, Handshake, UserCog,
  Settings, FileText, ChevronLeft, ChevronRight, ChevronDown, LogOut,
  Menu, Bell, DollarSign, FolderOpen, Building2, Mail, PenTool, Truck, X, UserSearch, Megaphone, Wrench
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import api from '@/lib/api';

const DEFAULT_LOGO = "https://customer-assets.emergentagent.com/job_12492ec0-1e26-4f4b-96d9-e0a655349093/artifacts/4uh3nwhf_image.png";

// Sidebar structure with groups. Each item has a stable `key` used by the
// role-based menu visibility settings (Settings → Sidebar Menu Visibility).
const sidebarConfig = [
  { key: 'dashboard', type: 'link', label: 'Dashboard', path: '/admin', icon: LayoutDashboard },
  {
    key: 'inventory', type: 'group', label: 'Inventory', icon: Package, adminOnly: false,
    children: [
      { key: 'inventory.all', label: 'All Inventory', path: '/admin/inventory', icon: Package },
      { key: 'inventory.consignment', label: 'Consignment', path: '/admin/consignment', icon: Truck, adminOnly: true },
    ],
  },
  { key: 'leads', type: 'link', label: 'Leads', path: '/admin/leads', icon: Megaphone, badgeKey: 'leads' },
  {
    key: 'clients', type: 'group', label: 'Clients', icon: Users, adminOnly: false,
    children: [
      { key: 'clients.all', label: 'All Clients', path: '/admin/clients', icon: Users },
      { key: 'clients.prospects', label: 'Prospects', path: '/admin/prospects', icon: UserSearch, adminOnly: true },
    ],
  },
  { key: 'applications', type: 'link', label: 'Applications', staffLabel: 'Track Applications', path: '/admin/deals', icon: Handshake, badgeKey: 'deals' },
  { key: 'financing', type: 'link', label: 'Financing Requests', path: '/admin/financing', icon: DollarSign, adminOnly: true },
  { key: 'documents', type: 'link', label: 'Documents', path: '/admin/documents', icon: FolderOpen },
  { key: 'lenders', type: 'link', label: 'Lenders', path: '/admin/lenders', icon: Building2, adminOnly: true },
  {
    key: 'system', type: 'group', label: 'System', icon: Wrench, adminOnly: true,
    children: [
      { key: 'system.notifications', label: 'Notifications', path: '/admin/notifications', icon: Bell },
      { key: 'system.staff', label: 'Staff', path: '/admin/staff', icon: UserCog },
      { key: 'system.cms', label: 'CMS', path: '/admin/cms', icon: FileText },
      { key: 'system.email_templates', label: 'Email Templates', path: '/admin/email-templates', icon: Mail },
      { key: 'system.credit_app', label: 'Credit App', path: '/admin/credit-app-template', icon: PenTool },
      { key: 'system.doc_templates', label: 'Doc Templates', path: '/admin/doc-templates', icon: FileText },
    ],
  },
  { key: 'settings', type: 'link', label: 'Settings', path: '/admin/settings', icon: Settings, adminOnly: true },
];

// Build a flat list of every (key, label) the visibility settings can toggle.
export const SIDEBAR_MENU_KEYS = sidebarConfig.flatMap(item => {
  const top = [{ key: item.key, label: item.label }];
  if (item.children) {
    return top.concat(item.children.map(c => ({ key: c.key, label: `${item.label} › ${c.label}` })));
  }
  return top;
});

function BadgeCount({ count }) {
  if (!count || count <= 0) return null;
  return (
    <span className="ml-auto min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-[#E31937] text-white text-[10px] font-bold px-1">
      {count > 99 ? '99+' : count}
    </span>
  );
}

function SidebarLink({ link, collapsed, active, displayLabel, badgeCount, onClick }) {
  const Icon = link.icon;
  return (
    <Link
      to={link.path}
      onClick={onClick}
      className={`flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-all relative ${
        active
          ? 'bg-[#E31937]/10 text-[#E31937] font-medium'
          : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
      }`}
      data-testid={`admin-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
      title={collapsed ? displayLabel : undefined}
    >
      <Icon className="w-4 h-4 flex-shrink-0" />
      {!collapsed && <span className="truncate">{displayLabel}</span>}
      {!collapsed && <BadgeCount count={badgeCount} />}
      {collapsed && badgeCount > 0 && (
        <span className="absolute right-1 top-0 min-w-[14px] h-[14px] flex items-center justify-center rounded-full bg-[#E31937] text-white text-[8px] font-bold px-0.5">
          {badgeCount > 99 ? '99+' : badgeCount}
        </span>
      )}
    </Link>
  );
}

function SidebarGroup({ item, collapsed, location, isAdmin, badges, getDisplayLabel, openGroups, toggleGroup, onClick, isMenuVisible }) {
  const Icon = item.icon;
  const isOpen = openGroups.includes(item.label);
  const children = item.children.filter(c => (!c.adminOnly || isAdmin) && (!isMenuVisible || isMenuVisible(c.key)));
  const hasActiveChild = children.some(c => location.pathname === c.path || (c.path !== '/admin' && location.pathname.startsWith(c.path)));

  if (collapsed) {
    // Show first child link when collapsed
    const first = children[0];
    if (!first) return null;
    return (
      <Link
        to={first.path}
        className={`flex items-center justify-center px-3 py-2 rounded-sm text-sm transition-all ${
          hasActiveChild ? 'bg-[#E31937]/10 text-[#E31937]' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
        }`}
        title={item.label}
      >
        <Icon className="w-4 h-4" />
      </Link>
    );
  }

  return (
    <div>
      <button
        onClick={() => toggleGroup(item.label)}
        className={`flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-all w-full ${
          hasActiveChild ? 'text-[#E31937] font-medium' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
        }`}
        data-testid={`admin-nav-group-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <Icon className="w-4 h-4 flex-shrink-0" />
        <span className="truncate">{item.label}</span>
        <ChevronDown className={`w-3.5 h-3.5 ml-auto transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="ml-4 pl-3 border-l border-gray-200 mt-0.5 space-y-0.5">
          {children.map(child => {
            const active = location.pathname === child.path || (child.path !== '/admin' && location.pathname.startsWith(child.path));
            const badgeCount = child.badgeKey ? badges[child.badgeKey] : 0;
            return (
              <SidebarLink
                key={child.path}
                link={child}
                collapsed={false}
                active={active}
                displayLabel={getDisplayLabel(child)}
                badgeCount={badgeCount}
                onClick={onClick}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState(DEFAULT_LOGO);
  const [badges, setBadges] = useState({});
  const [openGroups, setOpenGroups] = useState(['Inventory', 'Clients']);
  const [menuVisibility, setMenuVisibility] = useState({}); // { [role]: { [key]: false } }
  const isAdmin = user?.role === 'admin';
  const userRole = (user?.role || 'staff').toLowerCase();

  // Resolve whether a menu key is visible for the current user.
  // Settings + the visibility editor are ALWAYS visible to admins (lockout guard).
  const isMenuVisible = (key) => {
    if (isAdmin && (key === 'settings' || key === 'system' || key === 'system.staff')) return true;
    const roleCfg = menuVisibility[userRole] || {};
    return roleCfg[key] !== false; // default ON when missing
  };

  const toggleGroup = (label) => {
    setOpenGroups(prev => prev.includes(label) ? prev.filter(g => g !== label) : [...prev, label]);
  };

  // Auto-open group if it contains the active page
  useEffect(() => {
    sidebarConfig.forEach(item => {
      if (item.type === 'group') {
        const hasActive = item.children.some(c => location.pathname === c.path || (c.path !== '/admin' && location.pathname.startsWith(c.path)));
        if (hasActive && !openGroups.includes(item.label)) {
          setOpenGroups(prev => [...prev, item.label]);
        }
      }
    });
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBadges = useCallback(async () => {
    try {
      const res = await api.get('/nav-badges');
      setBadges(res.data || {});
    } catch (e) { console.error('Nav badges load error:', e); }
  }, []);

  useEffect(() => {
    settingsAPI.getBranding().then(r => {
      if (r.data?.primary_logo) setLogoUrl(resolveImageUrl(r.data.primary_logo));
    }).catch(() => {});
  }, []);

  // Load role-based menu visibility settings (silent fallback to empty = all visible)
  useEffect(() => {
    api.get('/settings/menu-visibility').then(r => setMenuVisibility(r.data || {})).catch(() => setMenuVisibility({}));
  }, []);

  useEffect(() => {
    fetchBadges();
    const interval = setInterval(fetchBadges, 30000);
    return () => clearInterval(interval);
  }, [fetchBadges, location.pathname]);

  const getDisplayLabel = (link) => {
    if (!isAdmin && link.staffLabel) return link.staffLabel;
    return link.label;
  };

  // Filter top-level items by role + admin's menu-visibility settings
  const visibleItems = sidebarConfig.filter(item => {
    if (item.adminOnly && !isAdmin) return false;
    if (!isMenuVisible(item.key)) return false;
    if (item.type === 'group') {
      const visibleChildren = item.children.filter(c => (!c.adminOnly || isAdmin) && isMenuVisible(c.key));
      return visibleChildren.length > 0;
    }
    return true;
  });

  const renderItem = (item, onClick) => {
    if (item.type === 'group') {
      return (
        <SidebarGroup
          key={item.label}
          item={item}
          collapsed={collapsed}
          location={location}
          isAdmin={isAdmin}
          badges={badges}
          getDisplayLabel={getDisplayLabel}
          openGroups={openGroups}
          toggleGroup={toggleGroup}
          onClick={onClick}
          isMenuVisible={isMenuVisible}
        />
      );
    }
    const active = location.pathname === item.path || (item.path !== '/admin' && location.pathname.startsWith(item.path));
    const badgeCount = item.badgeKey ? badges[item.badgeKey] : 0;
    return (
      <SidebarLink
        key={item.path}
        link={item}
        collapsed={collapsed}
        active={active}
        displayLabel={getDisplayLabel(item)}
        badgeCount={badgeCount}
        onClick={onClick}
      />
    );
  };

  return (
    <div className="min-h-screen flex bg-gray-50" data-testid="admin-layout">
      {/* Desktop Sidebar */}
      <aside className={`hidden md:flex flex-col ${collapsed ? 'w-16' : 'w-56'} bg-white border-r border-gray-200 transition-all duration-200 flex-shrink-0`}>
        <div className="relative p-4 flex items-center justify-center border-b border-gray-200 h-20">
          {!collapsed && <img src={logoUrl} alt="CTS" className="h-12 max-w-[160px] w-auto object-contain" />}
          <button onClick={() => setCollapsed(!collapsed)} className="absolute top-2 right-2 text-gray-500 hover:text-gray-900 p-1">
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>
        <nav className="flex-1 py-3 flex flex-col gap-0.5 px-2 overflow-y-auto">
          {visibleItems.map(item => renderItem(item, undefined))}
        </nav>
        <div className="p-3 border-t border-gray-200">
          <button
            onClick={() => { navigate('/'); setTimeout(() => logout(), 100); }}
            className="flex items-center gap-3 px-3 py-2 text-gray-500 hover:text-gray-900 text-sm w-full rounded-sm hover:bg-gray-100"
            data-testid="admin-logout"
          >
            <LogOut className="w-4 h-4" />
            {!collapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen overflow-x-hidden">
        {/* Top bar */}
        <header className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-3 sm:px-4 lg:px-6 flex-shrink-0">
          <div className="flex items-center gap-3">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" className="md:hidden h-8 w-8 p-0" data-testid="mobile-menu-toggle">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-0 bg-white">
                <SheetHeader className="p-4 border-b border-gray-200">
                  <SheetTitle className="sr-only">Navigation</SheetTitle>
                  <img src={logoUrl} alt="CTS" className="h-12 max-w-[200px] w-auto object-contain mr-auto" />
                </SheetHeader>
                <nav className="flex-1 py-3 flex flex-col gap-0.5 px-2 overflow-y-auto max-h-[calc(100vh-140px)]">
                  {visibleItems.map(item => renderItem(item, () => setMobileOpen(false)))}
                </nav>
                <div className="p-3 border-t border-gray-200 mt-auto">
                  <button
                    onClick={() => { setMobileOpen(false); navigate('/'); setTimeout(() => logout(), 100); }}
                    className="flex items-center gap-3 px-3 py-2.5 text-gray-500 hover:text-gray-900 text-sm w-full rounded-sm hover:bg-gray-100"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              </SheetContent>
            </Sheet>
            <div className="md:hidden">
              <img src={logoUrl} alt="CTS" className="h-7 max-w-[100px] w-auto object-contain" />
            </div>
            <h2 className="text-gray-900 text-sm font-medium hidden md:block">Admin Portal</h2>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-gray-500 hover:text-gray-900 text-xs">View Site</Link>
            <div className="text-right">
              <p className="text-gray-900 text-xs font-medium">{user?.name}</p>
              <p className="text-gray-400 text-[10px] uppercase">{user?.role}</p>
            </div>
          </div>
        </header>

        <main className="flex-1 p-3 sm:p-4 lg:p-6 overflow-auto bg-gray-50 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
